import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Input } from '../components/ui/input';
import { ChevronDown, HelpCircle, MessageSquare, Activity, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HelpPage() {
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    document.title = 'Help Center · Delphi AI';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Delphi AI Help Center — answers to common questions about credits, generation, The Oracle, and your account. Searchable FAQ.');
    axios.get(`${API}/support/faqs`).then(r => setFaqs(r.data?.faqs || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return faqs;
    return faqs
      .map(cat => ({
        ...cat,
        items: cat.items.filter(it => it.q.toLowerCase().includes(needle) || it.a.toLowerCase().includes(needle)),
      }))
      .filter(cat => cat.items.length);
  }, [faqs, q]);

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="help-page">
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-5xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center"><HelpCircle className="w-5 h-5 text-white" /></div>
              <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Help Center</h1>
            </div>
            <p className="text-sm text-white/50 ml-13">Answers to common questions about Delphi AI. Can&apos;t find what you need? <Link to="/contact" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">Contact support</Link>.</p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="relative mb-8">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              data-testid="help-search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search help articles..."
              className="pl-10 bg-[#12121a] border-white/[0.06] text-white h-12 text-base"
            />
          </div>

          {filtered.length === 0 && q && (
            <div className="text-center py-12 text-white/40">
              No results for &quot;<span className="text-white/70">{q}</span>&quot;.
              <div className="mt-3"><Link to="/contact" className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300"><MessageSquare className="w-4 h-4" />Ask us directly</Link></div>
            </div>
          )}

          {filtered.map((cat) => (
            <section key={cat.category} className="mb-8">
              <h2 className="text-xs uppercase tracking-[0.25em] text-orange-400/70 font-semibold mb-3">{cat.category}</h2>
              <div className="space-y-2">
                {cat.items.map((it, i) => {
                  const key = `${cat.category}-${i}`;
                  const isOpen = open[key];
                  return (
                    <div key={key} className="rounded-lg border border-white/[0.06] bg-[#12121a]/60 overflow-hidden">
                      <button
                        data-testid={`faq-q-${key}`}
                        onClick={() => setOpen(o => ({ ...o, [key]: !o[key] }))}
                        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors"
                      >
                        <span className="text-sm text-white/90 font-medium">{it.q}</span>
                        <ChevronDown className={`w-4 h-4 text-white/40 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 text-sm text-white/60 leading-relaxed border-t border-white/[0.04]" data-testid={`faq-a-${key}`}>
                          <p className="pt-3">{it.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/contact" className="block rounded-xl border border-white/[0.06] bg-gradient-to-br from-orange-500/[0.08] to-amber-500/[0.04] hover:from-orange-500/[0.12] p-5 transition-colors" data-testid="cta-contact">
              <MessageSquare className="w-5 h-5 text-orange-400 mb-2" />
              <p className="font-semibold text-white mb-1">Contact Support</p>
              <p className="text-xs text-white/50">We respond within 24 hours.</p>
            </Link>
            <Link to="/status" className="block rounded-xl border border-white/[0.06] bg-[#12121a]/60 hover:bg-[#12121a] p-5 transition-colors" data-testid="cta-status">
              <Activity className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="font-semibold text-white mb-1">System Status</p>
              <p className="text-xs text-white/50">Real-time provider availability.</p>
            </Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
